export type SLODeclaration = Record<PropertyKey, string>;

/*=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~
  Add or remove UFO experience key declarations in the sections below. After declaring a new key, or removing an
  existing one, you must also add or remove its SLO definition in the file `slo-definitions.ts`.

  Note: Declared keys and their SLO definitions are tied to the creation of actual monitoring resources. Therefore,
  each key must be used by exactly one route or SLO component. If necessary for experiments, a key may be used in more
  than one component if the SLO definition sets the "allowExperiments" flag.
  =~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~=~*/

/**
 * SLO keys to be used exclusively for page load tracking. Each declared key must be used in the route-to-key mapping
 * defined in `afm/rovo-extension/packages/extension-frontend/src/analytics/react-ufo/useReactUFORouteWatcher.ts`.
 */
export const RouteSLOKeys = {
  CHAT: "chat",
  HOME: "home",
  NTP_LOGIN_REDIRECT: "ntp-login-redirect",
  OPTIONS: "options",
  POPUP: "popup",
  UNINSTALL: "uninstall",
  WELCOME: "welcome",
} as const satisfies SLODeclaration;

/**
 * SLO keys for use with the `PerformanceSLO` component, which monitor the render performance of a segment of code.
 */
export const PerformanceSLOKeys = {
  APPLICATION_BOOTSTRAP: "application-bootstrap",
} as const satisfies SLODeclaration;

/**
 * SLO keys for use with the `ExperienceSLO` component, which monitors the render reliability (success vs. failure rate)
 * and, optionally, also the render performance of a segment of code.
 */
export const ExperienceSLOKeys = {
  ANNOUNCEMENT_BANNER: "announcement-banner",
  ANNOUNCEMENT_WIDGET: "announcement-widget",
  APP_ROOT: "app-root",
  BOOKMARKS_BAR: "bookmarks-bar",
  CHAT_CONTENT_FULLPAGE: "chat-content-fullpage",
  CHAT_PAGE: "chat-page",
  CONVERSATION_ASSISTANT_FULLPAGE: "conversation-assistant-fullpage",
  DISMISSIBLE_WIDGETS: "dismissible-widgets",
  GENERAL_OPTIONS_SECTION: "general-options-section",
  HOME_CONTENT: "home-content",
  NEW_TAB_PAGE_OPTIONS_SECTION: "new-tab-page-options-section",
  OPTIONS_FOOTER: "options-footer",
  OPTIONS_PAGE: "options-page",
  PAGE_LAYOUT: "page-layout",
  RECENT_WORK_WIDGET: "recent-work-widget",
  SEARCH_TAB: "search-tab",
  SEARCH_TAB_SELECTOR: "search-tab-selector",
  SEARCH_TAB_SELECTOR_WIDGET: "search-tab-selector-widget",
  SHORTCUTS: "shortcuts",
  SITE_SELECTOR: "site-selector",
  SITE_SELECTOR_WIDGET: "site-selector-widget",
  TOP_BAR: "top-bar",
  WELCOME_HEADER: "welcome-header",
} as const satisfies SLODeclaration;
